import { useId } from 'react'

import { FxIcon } from '@/components/fx'
import { cn } from '@/lib/utils'
import type { OnboardingQuestion } from './onboardApi'
import { isRenderableKind, isSelected, type Selections } from './onboardModel'

/**
 * One question from the server's catalogue.
 *
 * `multi` is the artboard's row of pressable chips — real `<button>`s with
 * `aria-pressed`, inside a group named by the prompt, so a screen reader
 * announces "Formulare auslesen, Umschaltknopf, gedrückt" rather than
 * reading six unrelated buttons. `single` is a native `<select>`, as drawn:
 * a listbox the platform already makes keyboard- and touch-friendly.
 *
 * A kind this build cannot draw keeps its prompt and says plainly that it
 * cannot be answered here. The alternative — leaving it out — would show a
 * shorter questionnaire that looks complete, and the person would have no
 * way of knowing something was missing.
 */
export function QuestionBlock({
  question,
  selections,
  onToggle,
  onSelect,
}: {
  question: OnboardingQuestion
  selections: Selections
  onToggle: (optionId: string) => void
  onSelect: (optionId: string) => void
}) {
  const labelId = useId()

  if (!isRenderableKind(question.kind)) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-body-strong text-text">{question.prompt}</p>
        <p className="flex items-center gap-2 text-small text-text-muted">
          <FxIcon name="info" size={18} />
          Diese Frage können wir in dieser Version noch nicht anzeigen. Bereits gespeicherte
          Antworten bleiben erhalten.
        </p>
      </div>
    )
  }

  if (question.kind === 'single') {
    const value = selections[question.id]?.[0] ?? ''
    return (
      <div className="flex flex-col gap-2">
        <label htmlFor={labelId} className="text-body-strong text-text">
          {question.prompt}
        </label>
        <select
          id={labelId}
          value={value}
          onChange={(event) => onSelect(event.target.value)}
          className="h-control w-full rounded-md bg-ground px-4 text-body text-text shadow-raised"
        >
          <option value="">Bitte wählen</option>
          {question.options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <p id={labelId} className="text-body-strong text-text">
        {question.prompt}
      </p>
      <div role="group" aria-labelledby={labelId} className="flex flex-wrap gap-3">
        {question.options.map((option) => {
          const selected = isSelected(selections, question.id, option.id)
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={selected}
              onClick={() => onToggle(option.id)}
              className={cn(
                'inline-flex h-9 items-center gap-2 rounded-md px-4 text-small shadow-raised',
                selected
                  ? 'bg-primary-subtle font-semibold text-primary-text ring-2 ring-inset ring-primary'
                  : 'bg-surface text-text',
              )}
            >
              {selected && <FxIcon name="check" size={18} />}
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
